var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var __TvSymbolConvertor_sessionConvertor;
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { InstrumentUtils } from '@shared/utils/Instruments/InstrumentUtils';
import { TvSessionIdEnum, VisiblePlotsSetEnum } from '../TradingViewPrimitives/TvEnums';
import { TvSearchSymbolResultItem } from '../TradingViewPrimitives/TvSearchSymbolResultItem';
import { TvSymbol } from '../TradingViewPrimitives/TvSymbol';
import { TvSessionConvertor } from './TvSessionConvertor';
import { Periods } from '@shared/utils/History/TFInfo';
import { TvSymbolInfoPriceSource } from '../TradingViewPrimitives/TvSymbolInfoPriceSource';
import { Resources } from '@shared/localizations/Resources';
import { TvTimeZoneHelper } from '../Helpers/TvTimeZoneHelper';
import { TvInteriorIdCache } from '../Caches/TvInteriorIdCache';
import { DataCache } from '../../DataCache';
import { InstrumentTypes } from '@shared/utils/Instruments/InstrumentTypes';
import { ProductType } from '@shared/utils/Instruments/ProductType';
import { TvDurationConvertor } from './TvDurationConvertor';
import { Decimal } from 'decimal.js';
import { HistoryType } from '@shared/utils/History/HistoryType';
import { SymbolSvgLogoManager } from '../../UtilsClasses/SymbolSvgLogoManager';
import { TvAccountHelper } from '../Helpers/TvAccountHelper';
import { InstrumentSpecificType } from '@shared/utils/Instruments/InstrumentSpecificType';
import { DateTimeUtils } from '@shared/utils/Time/DateTimeUtils';
import { TimeZoneInfo } from '@shared/utils/Time/TimeZoneInfo';
import { BaseSettings } from '../../Settings/BaseGeneralSettingsWrapper';
const fakeTvSymbolName = 'fakeTVSymbol';
export class _TvSymbolConvertor {
    constructor() {
        __TvSymbolConvertor_sessionConvertor.set(this, new TvSessionConvertor());
    }
    convertToTvSymbol(instrument, extension) {
        var _a, _b;
        const session = instrument.Session;
        const holidays = __classPrivateFieldGet(this, __TvSymbolConvertor_sessionConvertor, "f").getHolidays(session);
        const currentAcc = TvAccountHelper.getCurrentAccount();
        const tvSubsessions = __classPrivateFieldGet(this, __TvSymbolConvertor_sessionConvertor, "f").getSubsessions(session, instrument.isAllowOTHTrading);
        const subsessionID = (_a = extension === null || extension === void 0 ? void 0 : extension.session) !== null && _a !== void 0 ? _a : TvSessionIdEnum.regular;
        const tvSubSession = tvSubsessions.find((x) => x.id === subsessionID);
        const tvSession = (_b = tvSubSession === null || tvSubSession === void 0 ? void 0 : tvSubSession.session) !== null && _b !== void 0 ? _b : '';
        const corrections = tvSubSession === null || tvSubSession === void 0 ? void 0 : tvSubSession.getCorrection();
        const precision = instrument.getPrecision();
        const priceScale = new Decimal(10).pow(precision).toNumber();
        const mintickSize = instrument.getPointSize(null);
        const minmov = new Decimal(mintickSize).mul(priceScale).toNumber();
        const tvSymbol = new TvSymbol();
        tvSymbol.ticker = this.getTicker(instrument);
        tvSymbol.name = this.getSymbolName(instrument);
        tvSymbol.description = instrument.DescriptionValue();
        tvSymbol.type = this.getSymbolType(instrument.InstrType);
        tvSymbol.pricescale = priceScale;
        tvSymbol.minmov = minmov;
        tvSymbol.minmove2 = tvSymbol.type === TvInstrumentType.Forex ? 10 : 0;
        tvSymbol.volume_precision = instrument.getAmountPrecision(BaseSettings.isQuantityInLots, currentAcc, ProductType.General);
        tvSymbol.exchange = instrument.TradingExchange;
        tvSymbol.listed_exchange = instrument.TradingExchange;
        tvSymbol.sector = instrument.LocalizedSector;
        tvSymbol.industry = instrument.LocalizedIndustry;
        tvSymbol.currency_code = tvSymbol.original_currency_code = this.getSymbolCurrency(instrument);
        tvSymbol.session = tvSession;
        tvSymbol.session_holidays = holidays;
        tvSymbol.corrections = corrections;
        tvSymbol.subsession_id = subsessionID;
        tvSymbol.subsessions = tvSubsessions;
        tvSymbol.timezone = TvTimeZoneHelper.getTimeZoneForTV(session.TimeZoneID);
        tvSymbol.logo_urls = this.getLogoUrls(instrument);
        if (instrument.isOptionSymbol ||
            (instrument.isFuturesSymbol() &&
                instrument.InstrumentSpecificType !== InstrumentSpecificType.ContinuousContract)) {
            const serverDateNow = TimeZoneInfo.ConvertTimeFromUtc(new Date(), TimeZoneInfo.SERVER_TIME_ZONE); // converted to server`s date because ExpDateReal is in server timeZone
            const serverDateNowWithoutTime = DateTimeUtils.getDateWithoutTime(serverDateNow);
            if (instrument.ExpDateReal.getFullYear() > 2000 && instrument.ExpDateReal < serverDateNowWithoutTime) {
                tvSymbol.expired = true;
                tvSymbol.expiration_date = instrument.ExpDateReal.getTime() / 1000;
            }
        }
        const variableString = this.getVariableTickListString(instrument);
        if (variableString.length > 0)
            tvSymbol.variable_tick_size = variableString;
        // неважно какой период передавать, главное чтобы не тики
        const allowedHistoryTypes = instrument.DataCache.GetAllowedHistoryTypesByPeriod(Periods.MIN);
        const priceSources = [];
        for (const historyType of allowedHistoryTypes) {
            const historyTypeId = historyType;
            const historyTypeLocalization = Resources.getResource('chart.view.source' + historyType);
            const priceSource = new TvSymbolInfoPriceSource();
            priceSource.id = historyTypeId;
            priceSource.name = historyTypeLocalization;
            priceSources.push(priceSource);
        }
        if (priceSources.length > 0) {
            tvSymbol.price_sources = priceSources;
            const defChartHistoryType = instrument.DefaultChartHistoryType;
            tvSymbol.price_source_id = defChartHistoryType.toString();
            tvSymbol.visible_plots_set =
                defChartHistoryType === HistoryType.QUOTE_TRADES ? VisiblePlotsSetEnum.ohlcv : VisiblePlotsSetEnum.ohlc;
        }
        const quoteDelay = this.getQuoteDelay(instrument);
        if (quoteDelay > 0) {
            tvSymbol.data_status = 'delayed_streaming';
            tvSymbol.delay = quoteDelay * 60; // in seconds
        }
        // else if (instrument.QuoteCanGetSnapshot) {
        //     tvSymbol.data_status = 'endofday';
        //     tvSymbol.delay = -1; // for endofday
        // }
        TvInteriorIdCache.saveInteriorId(tvSymbol.ticker, instrument.GetInteriorID());
        return tvSymbol;
    }
    correctTvChartState(state, instrument) {
        state.symbol = instrument.GetInteriorID();
        state.shortName = this.getSymbolName(instrument);
        state.currencyId = this.getSymbolCurrency(instrument);
        state.sessionId = undefined;
    }
    getMarketExchange(instrument) {
        return instrument.TradingExchange;
    }
    getTicker(instrument) {
        if (TvInteriorIdCache.isTvChartMode()) {
            return instrument.GetInteriorID();
        }
        return instrument.DisplayName();
    }
    getSymbolName(instrument) {
        return instrument.DisplayName();
    }
    getFullname(instrument) {
        if (instrument == null) {
            return '';
        }
        const symbolWithoutRouteName = InstrumentUtils.RemoveRouteName(instrument.DisplayName());
        const exchange = instrument.TradingExchange;
        return `${exchange}:${symbolWithoutRouteName}`;
        // return instrument.DisplayName();
    }
    getLogoUrls(instrument) {
        if (instrument.LogoAddress) {
            return [instrument.LogoAddress];
        }
        const defaultLogo = SymbolSvgLogoManager.getDefaultImageInBase64(instrument.LogoName());
        return [defaultLogo];
    }
    convertToTvSearchSymbolResultItem(instrument) {
        const resultItem = new TvSearchSymbolResultItem();
        resultItem.description = instrument.DescriptionValue();
        resultItem.exchange = instrument.MarketDataExchange; // ?
        resultItem.ticker = this.getTicker(instrument);
        resultItem.symbol = this.getSymbolName(instrument);
        resultItem.full_name = this.getFullname(instrument);
        resultItem.type = this.getSymbolType(instrument.InstrType);
        resultItem.logo_urls = this.getLogoUrls(instrument);
        TvInteriorIdCache.saveInteriorId(resultItem.ticker, instrument.GetInteriorID());
        return resultItem;
    }
    isFakeSymbolName(symbolName) {
        return !symbolName || symbolName === fakeTvSymbolName;
    }
    getFakeSymbol() {
        const fakeTvSymbol = new TvSymbol();
        fakeTvSymbol.name = fakeTvSymbol.ticker = fakeTvSymbolName;
        fakeTvSymbol.session = '24x7';
        return fakeTvSymbol;
    }
    getInstrumentInfoAsync(symbol, account) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield new Promise((resolve, reject) => {
                try {
                    // Вызываем синхронный метод и возвращаем его результат
                    const info = this.getInstrumentInfo(symbol, account);
                    resolve(info);
                }
                catch (error) {
                    // Обработка возможных ошибок синхронного метода
                    reject(error);
                }
            });
        });
    }
    getInstrumentInfo(symbol, account) {
        // TODO: В дальнейшем планируется добавить динамический филд для выбора типа продукта
        const defaultProdutType = ProductType.General;
        const interiorId = TvInteriorIdCache.getInteriorId(symbol);
        const instrument = DataCache.getInstrumentByName(interiorId);
        const instrumentInfo = this.getDefaultInstrumentInfo();
        if (!instrument)
            return instrumentInfo;
        instrumentInfo.type = this.getSymbolType(instrument.InstrType);
        instrumentInfo.brokerSymbol = this.getSymbolName(instrument);
        instrumentInfo.description = instrument.DescriptionValue();
        // Quantity
        const lotStep = instrument.getLotStep(defaultProdutType, account);
        const minAmount = Math.max(instrument.getMinLot(defaultProdutType, account), lotStep);
        const maxAmount = instrument.getMaxLot(defaultProdutType, account);
        instrumentInfo.qty.default = minAmount;
        instrumentInfo.qty.min = minAmount;
        instrumentInfo.qty.max = maxAmount;
        instrumentInfo.qty.step = lotStep;
        // PipValue
        const pipSize = instrument.PipsSize;
        const crossPriceToAccountCurrency = DataCache.CrossRateCache.GetCrossPriceExp1Exp2(instrument.Exp2, account.BaseCurrency, account);
        const pipValue = pipSize * crossPriceToAccountCurrency;
        instrumentInfo.pipValue = pipValue;
        instrumentInfo.pipSize = pipSize;
        instrumentInfo.minTick = instrument.PointSize;
        instrumentInfo.lotSize = instrument.LotSize;
        instrumentInfo.units = Resources.getResource('TvTradingPlatform.Quantity');
        instrumentInfo.domVolumePrecision = instrument.getAmountPrecision(true, account, defaultProdutType);
        // leverage?: string;
        // limitPriceStep?: number;
        // stopPriceStep?: number;
        const allowedDurations = TvDurationConvertor.getAllowedDurationsForInstrumentAccount(instrument, account);
        instrumentInfo.allowedDurations = allowedDurations.map((x) => x.value);
        instrumentInfo.allowedOrderTypes = TvDurationConvertor.getAllowedTvOrderTypes(instrument, account);
        const variableString = this.getVariableTickListString(instrument);
        if (variableString.length > 0)
            instrumentInfo.variableMinTick = variableString;
        instrumentInfo.currency = instrument.Exp2;
        instrumentInfo.baseCurrency = instrument.Exp1;
        // quoteCurrency
        // bigPointValue
        // priceMagnifier
        return instrumentInfo;
    }
    getDefaultInstrumentInfo() {
        const qtyInfo = {
            min: 1,
            max: 1000000,
            step: 1
        };
        const instrumentInfo = {
            qty: qtyInfo,
            pipValue: 1,
            pipSize: 0.00001,
            minTick: 0.00001,
            lotSize: 0.1,
            type: 'forex',
            brokerSymbol: 'InstrumentInfoBrokerSymbol',
            description: 'InstrumentInfoDescription'
        };
        return instrumentInfo;
    }
    getVariableTickListString(instrument) {
        let variableString = '';
        const varTickList = instrument.VariableTickList;
        const listLength = varTickList.length;
        // the first interval (from negative infinity to 0) was skipped
        for (let i = 1; i < listLength; i++) {
            const tick = varTickList[i];
            if (i === listLength - 1) {
                variableString += `${tick.PointSize}`;
            }
            else {
                variableString += `${tick.PointSize} ${tick.RightBorder} `;
            }
        }
        return variableString;
    }
    getQuoteDelay(instrument) {
        if (instrument.HasDelay())
            return instrument.GetDelay();
        return DataCache.EntitlementManager.GetQuoteDelay(instrument);
    }
    getSymbolType(instType) {
        switch (instType) {
            case InstrumentTypes.FOREX:
                return TvInstrumentType.Forex;
            case InstrumentTypes.EQUITIES:
                return TvInstrumentType.Stock;
            case InstrumentTypes.EQUITIES_CFD:
            case InstrumentTypes.CFD_FUTURES:
                return TvInstrumentType.Cfd;
            case InstrumentTypes.FUTURES:
                return TvInstrumentType.Futures;
            case InstrumentTypes.INDICIES:
                return TvInstrumentType.Index;
            case InstrumentTypes.CRYPTO:
                return TvInstrumentType.Crypto;
            case InstrumentTypes.FIXED_INCOME:
                return TvInstrumentType.Bond;
            case InstrumentTypes.SPOT:
                return TvInstrumentType.Spot;
            case InstrumentTypes.OPTIONS:
                return TvInstrumentType.Options;
            case InstrumentTypes.FORWARD:
                return TvInstrumentType.Forward;
            case InstrumentTypes.SPREADBET:
                return TvInstrumentType.Spreadbet;
            case InstrumentTypes.ETF:
                return TvInstrumentType.Etf;
            case InstrumentTypes.TBILL:
                return TvInstrumentType.TBill;
            case InstrumentTypes.CORPORATE:
                return TvInstrumentType.Corporate;
            case InstrumentTypes.FUND:
                return TvInstrumentType.Fund;
            default:
                return TvInstrumentType.Undefined;
        }
    }
    getInstrumentTypes(symbolType) {
        const tvSymbolType = symbolType;
        switch (tvSymbolType) {
            case TvInstrumentType.Forex:
                return [InstrumentTypes.FOREX];
            case TvInstrumentType.Stock:
                return [InstrumentTypes.EQUITIES];
            case TvInstrumentType.Futures:
                return [InstrumentTypes.FUTURES];
            case TvInstrumentType.Index:
                return [InstrumentTypes.INDICIES];
            case TvInstrumentType.Crypto:
                return [InstrumentTypes.CRYPTO];
            case TvInstrumentType.Bond:
                return [InstrumentTypes.FIXED_INCOME];
            case TvInstrumentType.Spot:
                return [InstrumentTypes.SPOT];
            case TvInstrumentType.Options:
                return [InstrumentTypes.OPTIONS];
            case TvInstrumentType.Forward:
                return [InstrumentTypes.FORWARD];
            case TvInstrumentType.Spreadbet:
                return [InstrumentTypes.SPREADBET];
            case TvInstrumentType.Etf:
                return [InstrumentTypes.ETF];
            case TvInstrumentType.TBill:
                return [InstrumentTypes.TBILL];
            case TvInstrumentType.Corporate:
                return [InstrumentTypes.CORPORATE];
            case TvInstrumentType.Cfd:
                return [InstrumentTypes.CFD_FUTURES, InstrumentTypes.EQUITIES_CFD];
            case TvInstrumentType.Fund:
                return [InstrumentTypes.FUND];
            case TvInstrumentType.Undefined:
            default:
                return [InstrumentTypes.GENERAL];
        }
    }
    getSymbolCurrency(instrument) {
        return instrument === null || instrument === void 0 ? void 0 : instrument.Exp2;
    }
}
__TvSymbolConvertor_sessionConvertor = new WeakMap();
export const TvSymbolConvertor = new _TvSymbolConvertor();
var TvInstrumentType;
(function (TvInstrumentType) {
    TvInstrumentType["Undefined"] = "undefined";
    TvInstrumentType["Forex"] = "forex";
    TvInstrumentType["Stock"] = "stock";
    TvInstrumentType["Cfd"] = "cfd";
    TvInstrumentType["Futures"] = "futures";
    TvInstrumentType["Index"] = "index";
    TvInstrumentType["Crypto"] = "crypto";
    TvInstrumentType["Bond"] = "bond";
    TvInstrumentType["Spot"] = "spot";
    TvInstrumentType["Options"] = "options";
    TvInstrumentType["Forward"] = "forward";
    TvInstrumentType["Spreadbet"] = "spreadbet";
    TvInstrumentType["Etf"] = "etf";
    TvInstrumentType["TBill"] = "tbill";
    TvInstrumentType["Corporate"] = "corporate";
    TvInstrumentType["Fund"] = "fund";
})(TvInstrumentType || (TvInstrumentType = {}));
